import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useMemo,
} from "react";
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import type { PaletteOptions } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { lightPalette, darkPalette } from "../../theme/palette";
import { typography } from "../../theme/typography";

import { createComponents } from "../../theme/components";
import { brandColors } from "../../theme/brandColors";
import { useSessionQuery } from "../../features/auth/hooks";
import { useClientBranding } from "../hooks";
import type { ClientBranding } from "../api";

export type ThemeMode = "light" | "dark";

const createAppTheme = (palette: PaletteOptions) => {
  const theme = createTheme({
    shape: {
      borderRadius: 12,
    },
    palette,
    typography,
  });

  theme.components = {
    ...theme.components,
    ...createComponents(),
  };

  return theme;
};

export const lightTheme = createAppTheme(lightPalette);
export const darkTheme = createAppTheme(darkPalette);

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
  branding: ClientBranding | null;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const THEME_STORAGE_KEY = "theme-mode";
// PEAK4 2026 brand chart palette (PEAK4 Blue, Summit Green + secondary colours).
const DEFAULT_CHART_PALETTE = [
  brandColors.peak4Blue,
  brandColors.summitGreen,
  brandColors.performOrange,
  brandColors.fuelIndigo,
  brandColors.recoverPink,
  brandColors.focusLilac,
];
const DEFAULT_DARK_CHART_PALETTE = [
  brandColors.peak4Blue,
  brandColors.summitGreen,
  brandColors.performOrange,
  brandColors.fuelIndigo,
  brandColors.recoverPink,
  brandColors.focusLilac,
];

const applyBrandingToPalette = (
  palette: PaletteOptions,
  branding: ClientBranding | null,
  mode: ThemeMode
): PaletteOptions => {
  if (!branding) return palette;

  const isDark = mode === "dark";
  const primaryColor = isDark
    ? branding.dark_primary_color ?? branding.primary_color
    : branding.primary_color;
  const secondaryColor = isDark
    ? branding.dark_secondary_color ?? branding.secondary_color
    : branding.secondary_color;
  const backgroundColor = isDark
    ? branding.dark_background_color ?? branding.background_color
    : branding.background_color;
  const textColor = isDark
    ? branding.dark_text_color ?? branding.text_color
    : branding.text_color;

  const nextPalette: PaletteOptions = {
    ...palette,
    primary: {
      ...(palette.primary ?? {}),
      ...(primaryColor ? { main: primaryColor } : {}),
    },
    secondary: {
      ...(palette.secondary ?? {}),
      ...(secondaryColor ? { main: secondaryColor } : {}),
    },
    background: {
      ...(palette.background ?? {}),
      ...(backgroundColor
        ? {
            default: backgroundColor,
            paper: backgroundColor,
          }
        : {}),
    },
    text: {
      ...(palette.text ?? {}),
      ...(textColor ? { primary: textColor } : {}),
    },
  };

  return nextPalette;
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      return (stored as ThemeMode) || "light";
    } catch {
      return "light";
    }
  });

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  const setTheme = (newMode: ThemeMode) => {
    setMode(newMode);
  };

  const { data: session } = useSessionQuery();
  const clientId = session?.user?.client_id ?? null;
  const { data: brandingResponse } = useClientBranding(clientId);
  const branding = brandingResponse?.data ?? null;

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch (error) {
      console.error("Failed to save theme to localStorage:", error);
    }
  }, [mode]);

  const basePalette = mode === "light" ? lightPalette : darkPalette;
  const palette = useMemo(
    () => applyBrandingToPalette(basePalette, branding, mode),
    [basePalette, branding, mode]
  );

  const currentTheme = useMemo(
    () => createAppTheme(palette),
    [palette]
  );

  useEffect(() => {
    const root = document.documentElement;
    const isDark = mode === "dark";
    const chartPalette = isDark
      ? branding?.dark_chart_palette && branding.dark_chart_palette.length > 0
        ? branding.dark_chart_palette
        : DEFAULT_DARK_CHART_PALETTE
      : branding?.chart_palette && branding.chart_palette.length > 0
      ? branding.chart_palette
      : DEFAULT_CHART_PALETTE;
    const themePalette = currentTheme.palette;

    const accentColor = isDark
      ? branding?.dark_accent_color ??
        branding?.accent_color ??
        themePalette.secondary?.main ??
        DEFAULT_DARK_CHART_PALETTE[1]
      : branding?.accent_color ??
        themePalette.secondary?.main ??
        DEFAULT_CHART_PALETTE[1];

    root.style.setProperty(
      "--brand-primary",
      themePalette.primary?.main || ""
    );
    root.style.setProperty(
      "--brand-secondary",
      themePalette.secondary?.main || ""
    );
    root.style.setProperty("--brand-accent", accentColor || "");
    root.style.setProperty(
      "--brand-background",
      themePalette.background?.default || ""
    );
    root.style.setProperty("--brand-text", themePalette.text?.primary || "");

    const chartPrimary = isDark
      ? branding?.dark_chart_primary_color ??
        branding?.chart_primary_color ??
        chartPalette[0]
      : branding?.chart_primary_color ?? chartPalette[0];
    const chartSecondary = isDark
      ? branding?.dark_chart_secondary_color ??
        branding?.chart_secondary_color ??
        chartPalette[1]
      : branding?.chart_secondary_color ?? chartPalette[1];
    const chartAccent = isDark
      ? branding?.dark_chart_accent_color ??
        branding?.chart_accent_color ??
        chartPalette[2]
      : branding?.chart_accent_color ?? chartPalette[2];

    root.style.setProperty("--chart-primary", chartPrimary);
    root.style.setProperty("--chart-secondary", chartSecondary);
    root.style.setProperty("--chart-accent", chartAccent);
    chartPalette.forEach((color, index) => {
      root.style.setProperty(`--chart-palette-${index}`, color);
    });
  }, [branding, currentTheme, mode]);

  const value: ThemeContextType = {
    mode,
    toggleTheme,
    setTheme,
    branding,
  };

  return React.createElement(
    ThemeContext.Provider,
    { value },
    React.createElement(
      MuiThemeProvider,
      { theme: currentTheme },
      React.createElement(CssBaseline),
      children
    )
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
